import { FieldMappingFieldItem } from '@components/text-editors/addons/field-mapping';

const VARIABLE_PATTERN = /{{\s*([^}]+?)\s*}}/g;

/**
 * Converts a mapping label template from the API (e.g. "{{name}} - {{details[category]}}")
 * into a FieldMappingTextEditor-compatible default value with type-suffixed variable keys.
 *
 * Returns { value, variables } where:
 * - value: template with type-suffixed keys, e.g. "{{name_text}} - {{details[category]_text}}"
 * - variables: map from suffixed key to the matching field item
 */
export const buildMappingLabelDefaultValue = (
  mappingLabel: string,
  fields: FieldMappingFieldItem[]
): { value: string; variables: Record<string, FieldMappingFieldItem> } => {
  const getTypeSuffix = (key: string): string => {
    const field = fields.find((f) => f.label === key);
    return field?.type ?? 'text';
  };

  const value = mappingLabel.replace(
    VARIABLE_PATTERN,
    (_, key) => `{{${key}_${getTypeSuffix(key)}}}`
  );
  const variables = Array.from(mappingLabel.matchAll(VARIABLE_PATTERN)).reduce((acc, [, key]) => {
    const field = fields.find((f) => f.label === key);
    if (field) {
      acc[`${key}_${getTypeSuffix(key)}`] = field;
    }
    return acc;
  }, {} as Record<string, FieldMappingFieldItem>);

  return { value, variables };
};

/**
 * Extract path from endpoint URL by removing the base URL prefix.
 */
export const getPathFromEndpointUrl = (baseUrl: string, endpointUrl?: string): string => {
  const url = endpointUrl ?? '';
  if (!url) return '';

  const baseUrlTrimmed = baseUrl ? `${baseUrl.replace(/\/+$/, '')}/` : '';
  if (baseUrlTrimmed && url.startsWith(baseUrlTrimmed)) {
    return url.slice(baseUrlTrimmed.length);
  }
  if (baseUrl && url.startsWith(baseUrl)) {
    return url.slice(baseUrl.length).replace(/^\/+/, '');
  }
  // Fallback: if backend returned a full URL unrelated to baseUrl, or already stored a path.
  return url.replace(/^\/+/, '');
};

/**
 * Combine base URL and path into full endpoint URL.
 */
export const buildEndpointUrl = (baseUrl: string, path: string): string => {
  const baseUrlTrimmed = baseUrl ? baseUrl.replace(/\/+$/, '') : '';
  const pathTrimmed = path ? path.replace(/^\/+/, '') : '';
  return baseUrlTrimmed ? `${baseUrlTrimmed}/${pathTrimmed}` : pathTrimmed;
};

/**
 * Extract a value from nested data using a dot-separated path.
 * Example: extractValueFromPath({ response_metadata: { next_cursor: "abc" } }, "response_metadata.next_cursor") => "abc"
 */
export const extractValueFromPath = (data: unknown, path: string): unknown => {
  if (!data || !path) return undefined;
  let value: unknown = data;
  for (const key of path.split('.')) {
    if (value && typeof value === 'object' && key in value) {
      value = (value as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }
  return value;
};

/**
 * Convert dot notation to bracket notation for display.
 * Example: "response_metadata.next_cursor" => "response_metadata[next_cursor]"
 */
export const dotToBracketNotation = (path: string): string => {
  const parts = path.split('.');
  if (parts.length <= 1) return path;
  return `${parts[0]}${parts
    .slice(1)
    .map((p) => `[${p}]`)
    .join('')}`;
};

/**
 * Build a paginated URL by adding a query parameter with the token value.
 * Example: buildPaginatedUrl("https://api.example.com/users", "cursor", "abc123")
 *          => "https://api.example.com/users?cursor=abc123"
 */
export const buildPaginatedUrl = (
  baseUrl: string,
  paramName: string,
  tokenValue: string
): string => {
  try {
    const url = new URL(baseUrl);
    url.searchParams.set(paramName, tokenValue);
    return url.toString();
  } catch {
    // If URL is invalid, just append the query param
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}${encodeURIComponent(paramName)}=${encodeURIComponent(
      tokenValue
    )}`;
  }
};

export type FlattenedPath = {
  path: string;
  value: unknown;
  type: 'string' | 'number' | 'boolean' | 'null' | 'object' | 'array';
};

const getType = (value: unknown): FlattenedPath['type'] => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value as FlattenedPath['type'];
};

/**
 * Flatten an object into dot-separated paths with values and types.
 */
export const flattenObjectToPaths = (
  data: unknown,
  prefix = '',
  results: FlattenedPath[] = []
): FlattenedPath[] => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    if (prefix) results.push({ path: prefix, value: data, type: getType(data) });
    return results;
  }

  if (prefix) results.push({ path: prefix, value: data, type: 'object' });

  for (const [key, value] of Object.entries(data)) {
    flattenObjectToPaths(value, prefix ? `${prefix}.${key}` : key, results);
  }

  return results;
};
